import i18next from 'i18next';
import React from 'react';

import { Button } from '@commons/utils/styledLibraryComponents';
import { ExportButton } from '@commons/ExportButton';

import { EXTERNAL_ORDER_STATUS, PREPARED_EXTERNAL_ORDER_STATUSES } from '../constants';

/**
 * Header actions of the list. The export is available whatever the rights and keeps the same
 * icon-only button whatever is selected — the selection only drives what it carries, not how it
 * looks. The creation is hidden without the draft creation right.
 *
 * @param {Object} params
 * @param {boolean} params.canCreateDraft - Whether the user can create a draft order
 * @param {Function} params.onCreate - Creation handler
 * @param {Function} params.onExport - Export handler
 *
 * @returns {Array<Object>} The ListView actions
 */
export const getActions = ({ canCreateDraft, onCreate, onExport }) => {
  const actions = [
    {
      id: 'action-export',
      actionLabel: i18next.t('GENERAL.EXPORT_LIST'),
      handleAction: onExport,
      render: () => <ExportButton handleClick={onExport} hasLabel={false} />,
    },
  ];

  if (canCreateDraft) {
    actions.push({
      id: 'action-create',
      fixed: true,
      actionLabel: i18next.t('GENERAL.CREATE'),
      handleAction: onCreate,
      render: () => (
        <Button
          color={'inpulse-default'}
          handleClick={onCreate}
          icon={'/images/inpulse/add-white-small.svg'}
          label={i18next.t('GENERAL.CREATE')}
        />
      ),
    });
  }

  return actions;
};

/**
 * End-of-row actions. Each of them is gated both by its own right — an unauthorized action is
 * dropped from the menu entirely — and by the status the transition starts from.
 *
 * @param {Object} params - The rights and the handlers of each action
 *
 * @returns {Array<Object>} The ListView row actions
 */
export const getRowActions = ({
  canCreateDraft,
  canDeleteDraft,
  canRevertToDraft,
  canEditToComeAndInPreparation,
  canRevertToInPreparation,
  onDuplicate,
  onDelete,
  onRevertToDraft,
  onRevertToToCome,
  onRevertToInPreparation,
}) => {
  const rowActions = [];

  if (canCreateDraft) {
    rowActions.push({
      id: 'duplicate-order',
      actionLabel: () => i18next.t('EXTERNAL_ORDERS.LIST.DUPLICATE_ORDER'),
      actionIcon: () => '/images/inpulse/duplicate-black-small.svg',
      handleAction: onDuplicate,
    });
  }

  if (canDeleteDraft) {
    rowActions.push({
      id: 'delete-order',
      actionLabel: () => i18next.t('EXTERNAL_ORDERS.LIST.DELETE_ORDER'),
      actionIcon: () => '/images/inpulse/trash-black-small.svg',
      isHidden: ({ status }) => status !== EXTERNAL_ORDER_STATUS.DRAFT,
      handleAction: onDelete,
    });
  }

  if (canRevertToDraft) {
    rowActions.push({
      id: 'revert-order-to-draft',
      actionLabel: () => i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_DRAFT'),
      actionIcon: () => '/images/inpulse/pen-black-small.svg',
      isHidden: ({ status }) => status !== EXTERNAL_ORDER_STATUS.TO_COME,
      handleAction: onRevertToDraft,
    });
  }

  if (canEditToComeAndInPreparation) {
    rowActions.push({
      id: 'revert-order-to-to-come',
      actionLabel: () => i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_TO_COME'),
      actionIcon: () => '/images/inpulse/pen-black-small.svg',
      isHidden: ({ status }) => status !== EXTERNAL_ORDER_STATUS.IN_PREPARATION,
      handleAction: onRevertToToCome,
    });
  }

  if (canRevertToInPreparation) {
    rowActions.push({
      id: 'revert-order-to-in-preparation',
      actionLabel: () => i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_IN_PREPARATION'),
      actionIcon: () => '/images/inpulse/pen-black-small.svg',
      isHidden: ({ status }) => !PREPARED_EXTERNAL_ORDER_STATUSES.includes(status),
      handleAction: onRevertToInPreparation,
    });
  }

  return rowActions;
};
